import * as Headless from "@headlessui/react"
import React, { Fragment } from "react"
import { TouchTarget } from "@/components/catalyst/button"
import { usePage } from "@inertiajs/react"

import { Avatar } from "@/components/catalyst/avatar"
import { Link } from "@/components/catalyst/link"
import {
  Dropdown,
  DropdownButton,
  DropdownDivider,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
} from "@/components/catalyst/dropdown"
import { SidebarLayout } from "@/components/catalyst/sidebar-layout"
import {
  Navbar,
  NavbarItem,
  NavbarSection,
  NavbarSpacer,
} from "@/components/catalyst/navbar"
import {
  Sidebar,
  SidebarBody,
  SidebarFooter,
  SidebarHeader,
  SidebarHeading,
  SidebarItem,
  SidebarLabel,
  SidebarSection,
  SidebarSpacer,
} from "@/components/catalyst/sidebar"
import { Badge } from "@/components/catalyst/badge"

import {
  ArrowRightStartOnRectangleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  Cog8ToothIcon,
  LightBulbIcon,
  PlusIcon,
  ShieldCheckIcon,
  UserIcon,
} from "@heroicons/react/16/solid"
import {
  Cog6ToothIcon,
  HomeModernIcon,
  InboxIcon,
  MagnifyingGlassIcon,
  MegaphoneIcon,
  QuestionMarkCircleIcon,
  SparklesIcon,
  Square2StackIcon,
  WalletIcon,
  TicketIcon,
  LifebuoyIcon,
  UserGroupIcon,
  CursorArrowRaysIcon,
} from "@heroicons/react/20/solid"

import discordLogoBlackSvg from "@/assets/icons/discord_black.svg"

import sidestackLogoUrl from "@/assets/logo.png"

export default function Layout({ children }) {
  const { currentPath, currentAccount, currentUser } = usePage().props

  return (
    <SidebarLayout
      navbar={
        <Navbar>
          <NavbarSpacer />
          <NavbarSection>
            <img
              src={sidestackLogoUrl}
              alt="Sidestack"
              className="max-h-6 sm:max-h-8 object-scale-down w-fit"
            />
          </NavbarSection>
          <NavbarSpacer />

          <NavbarSection>
            <Dropdown>
              <DropdownButton as={NavbarItem}>
                <Avatar src={currentAccount.logoUrl} square />
              </DropdownButton>
              <DropdownMenu className="min-w-64" anchor="bottom end">
                <DropdownItem
                  method="delete"
                  href="/logout"
                  as="button"
                  type="button"
                >
                  <ArrowRightStartOnRectangleIcon />
                  <DropdownLabel>Logout</DropdownLabel>
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </NavbarSection>
        </Navbar>
      }
      sidebar={
        <Sidebar>
          <SidebarHeader className="hidden lg:block">
            <SidebarSection className="justify-left px-2 py-1 sm:py-2">
              <Link href="/dashboard">
                <img
                  src={sidestackLogoUrl}
                  alt="Sidestack"
                  className="max-h-7 sm:max-h-8 object-scale-down w-fit"
                />
              </Link>
            </SidebarSection>
          </SidebarHeader>

          <SidebarBody>
            <SidebarSection>
              {currentAccount.plan.discordIntegration &&
                !currentAccount.discordOnboardingCompleted && (
                  <Headless.CloseButton as={Fragment}>
                    <Link
                      href="/dashboard/discord/onboarding"
                      className={`mb-5 bg-orange-100 text-orange-500 hover:text-orange-500 group flex gap-x-3 rounded-lg p-2 text-sm leading-6 font-medium`}
                    >
                      <TouchTarget>
                        <LifebuoyIcon className="h-6 w-6 shrink-0 text-orange-500" />
                        Getting Started
                      </TouchTarget>
                    </Link>
                  </Headless.CloseButton>
                )}

              {currentAccount.plan.discordIntegration && (
                <SidebarItem
                  href="/dashboard/subscribers"
                  current={currentPath === "/dashboard/subscribers"}
                >
                  <UserGroupIcon />
                  <SidebarLabel>Subscribers</SidebarLabel>
                </SidebarItem>
              )}

              <SidebarItem
                href="/dashboard/settings"
                current={currentPath.startsWith("/dashboard/settings")}
              >
                <Cog6ToothIcon />
                <SidebarLabel>Settings</SidebarLabel>
              </SidebarItem>
              <a
                href={`https://forms.reform.app/yiAcTl/sidestack-help/NEsRp4?3e52086a-d512-4533-98f4-6eaa00fbf071=${currentUser.email}`}
                target="_blank"
              >
                <SidebarItem>
                  <QuestionMarkCircleIcon />
                  <SidebarLabel>Help</SidebarLabel>
                </SidebarItem>
              </a>
            </SidebarSection>

            <SidebarSection>
              <SidebarHeading>Substack Tools</SidebarHeading>

              <SidebarItem
                href="/dashboard/discord/home"
                current={currentPath === "/dashboard/discord/home"}
              >
                <img src={discordLogoBlackSvg} className="w-5" />
                <SidebarLabel>Discord Integration</SidebarLabel>
              </SidebarItem>

              <SidebarItem
                href="/dashboard/buy_button/products"
                current={currentPath.startsWith(
                  "/dashboard/buy_button/products"
                )}
              >
                <CursorArrowRaysIcon />
                <SidebarLabel className="flex gap-2 items-center">
                  Buy Buttons
                </SidebarLabel>
              </SidebarItem>
            </SidebarSection>

            <SidebarSpacer />

            <SidebarSection>
              <a
                href={`https://forms.reform.app/yiAcTl/sidestack-feedback/NEsRp4?9b0b8ab8-5d2e-499a-b179-6c7d2d6fb593=${currentUser.email}`}
                target="_blank"
                className="cursor-pointer"
              >
                <SidebarItem className="cursor-pointer">
                  <MegaphoneIcon />
                  <SidebarLabel>Send Feedback</SidebarLabel>
                </SidebarItem>
              </a>
              {/* <a href="https://headwayapp.co/sidestack-io-changelog">
                <SidebarItem>
                  <SparklesIcon />
                  <SidebarLabel>Changelog</SidebarLabel>
                </SidebarItem>
              </a> */}
            </SidebarSection>
          </SidebarBody>
          <SidebarFooter className="max-lg:hidden">
            <Dropdown>
              <DropdownButton as={SidebarItem}>
                <span className="flex min-w-0 items-center gap-3">
                  <Avatar
                    src={currentAccount.logoUrl}
                    className="size-10"
                    square
                    alt=""
                  />
                  <span className="min-w-0">
                    <span className="block truncate text-sm/5 font-medium text-zinc-950 dark:text-white">
                      {currentAccount.name}
                    </span>
                    <span className="block truncate text-xs/5 font-normal text-zinc-500 dark:text-zinc-400">
                      {currentUser.email}
                    </span>
                  </span>
                </span>
                <ChevronUpIcon />
              </DropdownButton>
              <DropdownMenu className="min-w-64" anchor="top start">
                <DropdownItem
                  method="delete"
                  href="/logout"
                  as="button"
                  type="button"
                >
                  <ArrowRightStartOnRectangleIcon />
                  <DropdownLabel>Logout</DropdownLabel>
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </SidebarFooter>
        </Sidebar>
      }
    >
      {children}
    </SidebarLayout>
  )
}
